import {
	CHECKED,
	HEPHON,
	vendorBySlug,
	vsTitle,
} from "@marketing/compare/vendors";
import {
	CompareTable,
	InShort,
	Prose,
	RelatedPages,
} from "@marketing/components/answer";
import {
	type Detail,
	DetailGrid,
	marketingHead,
	PageIntro,
	Section,
} from "@marketing/components/blocks";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { siteOrigin } from "@shared/lib/meta";
import { createFileRoute } from "@tanstack/react-router";

const PATH = "/best-ai-shopping-assistants";

interface Entry {
	name: string;
	/** Its comparison page and that page's title, or none for Hephon Commerce itself. */
	compare?: string;
	compareTitle?: string;
	url: string;
	summary: string;
	bestFor: string;
	strengths: string[];
	limits: string[];
}

interface Group {
	id: string;
	title: string;
	lead: string;
	entries: Entry[];
}

/** An entry from a vendor's fact sheet, so the list and the comparison pages never disagree. */
function entry(slug: string): Entry {
	const vendor = vendorBySlug(slug);
	if (!vendor) {
		throw new Error(`No fact sheet for ${slug}`);
	}
	return {
		name: vendor.name,
		compare: `/compare/${vendor.slug}`,
		compareTitle: vsTitle(vendor),
		url: vendor.url,
		summary: vendor.summary,
		bestFor: vendor.bestFor,
		strengths: vendor.stronger.slice(0, 2),
		limits: vendor.limits,
	};
}

const HEPHON_ENTRY: Entry = {
	name: HEPHON.name,
	url: `${siteOrigin}/`,
	summary:
		"An AI shopping assistant for supermarkets, grocery delivery and quick commerce in Saudi Arabia and the UAE, built by Hephon in Dubai, the team behind Panda's in-app Saudi-dialect shopping assistant.",
	bestFor: HEPHON.bestFor,
	strengths: HEPHON.strengths.slice(0, 2),
	limits: HEPHON.limits,
};

const GROUPS: Group[] = [
	{
		id: "gulf-title",
		title: "Gulf and Arabic-first assistants",
		lead: "Best when your shoppers write and speak Arabic and live on WhatsApp.",
		entries: [
			entry("yellow-ai"),
			HEPHON_ENTRY,
			entry("nateq"),
			entry("thikaa"),
			entry("arabybot"),
			entry("askit"),
		],
	},
	{
		id: "search-title",
		title: "Search and discovery platforms",
		lead: "Best when site search is your biggest lever and your shoppers buy on the web and in your app.",
		entries: [
			entry("constructor"),
			entry("bloomreach-clarity"),
			entry("zoovu"),
		],
	},
	{
		id: "ecommerce-title",
		title: "Ecommerce sales and support agents",
		lead: "Best for online brands on Shopify and similar platforms, with support tickets to deflect.",
		entries: [entry("alhena"), entry("rep-ai")],
	},
	{
		id: "other-title",
		title: "Other routes",
		lead: "Building your own, and the assistants shoppers bring themselves.",
		entries: [entry("build-vs-buy"), entry("superchat")],
	},
];

const ENTRIES = GROUPS.flatMap((group) => group.entries);

const VERDICT: Detail[] = [
	{
		title: "Large online grocer, English-first, search-led",
		text: "Constructor: grocery search, store-level availability, substitutes, and recipes built from what is in stock. Bloomreach Clarity if you already run Bloomreach.",
	},
	{
		title: "Shopify or DTC brand",
		text: "Rep AI for a low-cost sales agent from $12 a month. Alhena for sales and support together, more channels, voice, and SOC 2 Type 2.",
	},
	{
		title: "Complex products, not groceries",
		text: "Zoovu: guided selling, product quizzes and product data enrichment, in over 200 languages.",
	},
	{
		title: "Gulf enterprise, service first",
		text: "Yellow.ai: one platform for service, loyalty and campaigns, with Lulu Hypermarket's WhatsApp assistant as a reference.",
	},
	{
		title: "Gulf store on Salla or Zid",
		text: "Thikaa for a Salla-native WhatsApp bot from $9.99 a month. Nateq for a support inbox with Salla and Zid connectors.",
	},
	{
		title: "Small grocer taking WhatsApp orders from a price sheet",
		text: "ArabyBot: list ordering, delivery zones and slots, from $10 a month.",
	},
	{
		title: "Gulf supermarket chain with branches, an app and phone orders",
		text: "Hephon Commerce: Gulf Arabic and Arabizi, each branch's prices, recipes with pack sizes, WhatsApp checkout and phone ordering.",
	},
	{
		title: "Grocer with its own AI team",
		text: "A custom build on Azure OpenAI or OpenAI, as Grandiose did with GrandChef, if you can maintain and test it yourself.",
	},
];

const TESTS: Detail[] = [
	{
		title: "Pack sizes",
		text: 'Ask for "kabsa for 10" or "2 kilos of rice". A pass picks packs that cover the amount, says how many of each, and the basket total adds up.',
	},
	{
		title: "Branch prices",
		text: "Give two branches different prices and stock, then ask the same question from each. A pass shows each branch its own price and hides what it cannot deliver.",
	},
	{
		title: "Dialect and Arabizi",
		text: 'Send "abgha 7aleeb almarai kbeer", a Saudi voice note and an Arabic-English mix. A pass finds the right product and answers in the script it was asked in.',
	},
	{
		title: "Out-of-stock substitution",
		text: "Ask for an item your branch has run out of. A pass says so plainly, offers a close substitute from the same category, and asks before swapping.",
	},
	{
		title: "Confirm before order",
		text: 'Type or say "yes, order it". A pass reads the basket back and places nothing until an explicit confirm step: a tap, a checkout page or a keypad press.',
	},
];

const SUMMARY_COLUMNS = [
	"Assistant",
	"Built for",
	"Arabic dialects",
	"WhatsApp ordering",
	"Voice calls",
	"Public prices",
];

const SUMMARY_ROWS = [
	[
		"Constructor",
		"Grocery and retail site search, web and app",
		"Not stated publicly",
		"Not stated publicly",
		"Not stated publicly",
		"No",
	],
	[
		"Bloomreach Clarity",
		"Conversational layer on Bloomreach, website",
		"Not stated publicly",
		"Not stated publicly",
		"Not stated publicly",
		"No",
	],
	[
		"Zoovu",
		"Guided selling for complex products",
		"200+ languages; dialects not stated",
		"Not stated publicly",
		"Not stated publicly",
		"No, quote-based",
	],
	[
		"Alhena",
		"Ecommerce sales and support",
		"90+ languages; Arabic not named",
		"Channel listed; ordering not stated",
		"Yes, phone and SIP",
		"Yes, free to $999 a month",
	],
	[
		"Rep AI",
		"Shopify sales agent",
		"Not stated publicly",
		"Add-on channel; ordering not stated",
		"Not stated publicly",
		"Yes, from $12 a month",
	],
	[
		"Yellow.ai",
		"Enterprise customer service",
		"Arabic; dialects not stated",
		"Pickup orders, tracking, loyalty (Lulu)",
		"Yes",
		"Free tier; Enterprise by quote",
	],
	[
		"Nateq",
		"Gulf support inbox",
		"Gulf, Egyptian, Levantine, Maghrebi, Franco",
		"Product messages and payment links",
		"Yes",
		"Yes, from $15 a seat",
	],
	[
		"Thikaa",
		"Salla stores and small retailers",
		"Five dialects and Arabizi",
		"Orders with payment links",
		"Yes, WhatsApp and phone",
		"Yes, from $9.99 a month",
	],
	[
		"ArabyBot",
		"Small stores ordering on WhatsApp",
		"Six, including Gulf",
		"List orders confirmed in chat",
		"Voice notes; calls not stated",
		"Yes, from $10 a month",
	],
	[
		"AskIt",
		"Retail chains, website and in store",
		"Arabic; no dialect claim",
		"Not stated publicly",
		"Voice channel listed",
		"Not stated publicly",
	],
	[
		"Hephon Commerce",
		"Grocery ordering in app, WhatsApp and phone",
		"Saudi, Gulf, Lebanese and Arabizi",
		"Cards, basket, tap to confirm",
		"Yes, keypad confirms",
		"No, proposal per retailer",
	],
	[
		"Custom build",
		"Retailers with an in-house AI team",
		"What you build and test",
		"What you build",
		"What you build",
		"Cloud usage plus your team",
	],
	[
		"Superchat",
		"Shoppers (a personal assistant)",
		"Not stated publicly",
		"Shopper-side beta",
		"Voice in the app",
		"Yes, $9.99 a month (consumer)",
	],
];

const FAQ = [
	{
		q: "What is an AI shopping assistant for grocery?",
		a: "An assistant that talks with shoppers in their own words, searches the store's catalog, and builds a basket. For grocery it has to handle pack sizes, branch prices and stock, substitutions, budgets and recipes, and it should never place an order without the shopper's confirmation.",
	},
	{
		q: "What is the best AI shopping assistant for a supermarket in Saudi Arabia or the UAE?",
		a: "It depends on the retailer. For a chain with branches, an app and phone orders, Hephon Commerce is built for that job. For a Salla store, Thikaa or Nateq. For a small grocer on WhatsApp, ArabyBot. For service-first enterprise, Yellow.ai. Run the five tests on this page before you decide.",
	},
	{
		q: "Which AI shopping assistants understand Gulf Arabic and Arabizi?",
		a: "On their own pages: Nateq names Gulf Arabic and Franco-Arabic, Thikaa names Gulf Arabic and Arabizi, ArabyBot names Gulf Arabic, and Hephon Commerce understands Saudi and Gulf Arabic, Lebanese Arabic and Arabizi. Yellow.ai and AskIt support Arabic without naming dialects. The global vendors do not state Arabic dialects publicly.",
	},
	{
		q: "Which ones take orders on WhatsApp?",
		a: "ArabyBot, Thikaa and Hephon Commerce describe taking orders in a WhatsApp chat. Nateq sends payment links, and Yellow.ai's Lulu assistant handles in-store pickup orders, tracking and loyalty.",
	},
	{
		q: "Which AI shopping assistants publish their prices?",
		a: "Rep AI, Alhena, Nateq, Thikaa and ArabyBot publish prices, and Yellow.ai has a free tier. Constructor, Bloomreach, Zoovu, AskIt and Hephon Commerce price by proposal.",
	},
	{
		q: "Should we build our own on Azure OpenAI or OpenAI?",
		a: "If you have an AI team that can build search, dialects, channels, a confirmation step and tests, and keep them working as models change, building is a real option: Grandiose built GrandChef with Microsoft. Otherwise, buying gets you live sooner, and you can still build on the vendor's API later.",
	},
	{
		q: "Is this list biased towards Hephon Commerce?",
		a: "We make Hephon Commerce, so read it with that in mind. Every vendor fact links to the vendor's own page, our limits are listed next to everyone else's, and the five tests work the same on any assistant, including ours.",
	},
];

export const Route = createFileRoute("/_marketing/best-ai-shopping-assistants")(
	{
		head: () =>
			marketingHead({
				title: "Best AI Shopping Assistants for Grocery Retailers (2026)",
				description:
					"The best AI shopping assistants for grocery retailers in 2026: 13 options compared on Arabic, WhatsApp orders, branch prices and pricing, with 5 tests to run.",
				path: PATH,
				breadcrumb: ["Best AI shopping assistants"],
				faq: FAQ,
				updated: CHECKED,
				jsonLd: [
					{
						"@type": "ItemList",
						"@id": `${siteOrigin}${PATH}#list`,
						name: "Best AI shopping assistants for grocery retailers (2026)",
						itemListOrder: "https://schema.org/ItemListUnordered",
						numberOfItems: ENTRIES.length,
						itemListElement: ENTRIES.map((item, i) => ({
							"@type": "ListItem",
							position: i + 1,
							name: item.name,
							url: item.compare
								? `${siteOrigin}${item.compare}`
								: item.url,
						})),
					},
				],
			}),
		component: BestOfPage,
	},
);

function BestOfPage() {
	return (
		<>
			<PageIntro
				kicker="Buyer's guide"
				title="The best AI shopping assistants for grocery retailers in 2026"
				lead="Thirteen options, Gulf grocery first, then by the kind of retailer each one suits, with what they do well, their limits, and five tests to run on your own catalog."
			/>

			<InShort updated={CHECKED}>
				<p>
					The best AI shopping assistant depends on the retailer. For
					a Gulf supermarket chain whose shoppers order in Gulf Arabic
					or Arabizi, in its app, on WhatsApp and by phone, from a
					branch: Hephon Commerce, or Yellow.ai for a service-first
					enterprise. Thikaa, Nateq or ArabyBot suit smaller Gulf
					stores on Salla or WhatsApp, Constructor large search-led
					online grocers, and Rep AI or Alhena Shopify brands.
				</p>
				<p>
					We make Hephon Commerce, so judge it by the five tests
					below, like everyone else on this list.
				</p>
			</InShort>

			<Section
				id="verdict-title"
				title="Which one fits your kind of retailer?"
				lead="The short verdict. Each pick links to its full entry and comparison below."
			>
				<DetailGrid items={VERDICT} columns={4} />
			</Section>

			<Section
				id="tests"
				tone="mist"
				title="Five tests to run on your own catalog"
				lead="Demos run on catalogs chosen to look good. These five tests use yours, and each takes a few minutes in any vendor's trial or pilot."
			>
				<DetailGrid items={TESTS} columns={3} tone="mist" />
				<Prose className="mt-12 mx-0">
					<p>
						Grocery is harder than most ecommerce for an assistant.
						A basket has thirty items, not one. The same product has
						five pack sizes, a price per branch and a stock level
						that changes by the hour. Shoppers in the Gulf write in
						Arabic, in Arabizi and in English, often in one message,
						and many would rather send a voice note or call. An
						assistant that does well on a fashion store can still
						fail every one of these tests, so run them before you
						sign.
					</p>
				</Prose>
			</Section>

			{GROUPS.map((group, gi) => (
				<Section
					key={group.id}
					id={group.id}
					title={group.title}
					lead={group.lead}
					tone={gi % 2 === 1 ? "mist" : "white"}
				>
					<ol className="space-y-14">
						{group.entries.map((item) => (
							<EntryCard key={item.name} item={item} />
						))}
					</ol>
				</Section>
			))}

			<Section
				id="summary-title"
				title="The 13 options side by side"
				lead="From each vendor's own pages, checked on 5 November 2026. Not stated publicly does not mean missing."
			>
				<CompareTable columns={SUMMARY_COLUMNS} rows={SUMMARY_ROWS} />
			</Section>

			<Section
				id="method-title"
				tone="mist"
				title="How we picked and what we checked"
			>
				<Prose className="mx-0">
					<p>
						We picked the vendors that AI assistants and search
						results name when grocery and Gulf retailers ask for an
						AI shopping assistant, an Arabic shopping chatbot or
						WhatsApp ordering, and the global vendors that hold the
						AI shopping assistant category. Instacart, Mealz and
						SideChef were considered but left out, because we could
						not check enough public facts about their shopping
						assistants for this edition.
					</p>
					<p>
						For each vendor we read its product pages,
						documentation, pricing page and llms.txt file, and
						launch coverage for Superchat and Grandiose's GrandChef,
						and checked the same 15 points for each, from Arabic
						dialects to pricing and data. Where a page did not say,
						we wrote "not stated publicly". Every fact links to its
						source on the vendor's{" "}
						<a href="/compare">comparison page</a>.
					</p>
					<p>
						Last checked on 5 November 2026. We make Hephon
						Commerce, so judge us by the tests, not by our place on
						the list. If a fact is wrong or out of date, write to
						hello@hephon.ai and we will fix it.
					</p>
				</Prose>
			</Section>

			<Faq
				items={FAQ}
				title="Questions about choosing an AI shopping assistant."
			/>
			<RelatedPages
				links={[
					{ href: "/compare", label: "All comparisons" },
					{ href: "/", label: "AI shopping assistant" },
					{
						href: "/how-it-works",
						label: "How the AI shopping agent works",
					},
					{ href: "/shoppable-recipes", label: "Shoppable recipes" },
					{ href: "/channels/whatsapp", label: "WhatsApp ordering" },
					{
						href: "/arabic-chatbot-for-ecommerce",
						label: "Arabic chatbot for ecommerce",
					},
				]}
			/>
			<Closing />
		</>
	);
}

function EntryCard({ item }: { item: Entry }) {
	const position = ENTRIES.indexOf(item) + 1;
	return (
		<li className="mk-reveal max-w-3xl border-(--mk-line) border-t pt-6">
			<h3 className="mk-h3">
				{position}. {item.name}
			</h3>
			<p className="mt-3 text-(--mk-ink-2) text-[17px] leading-relaxed">
				{item.summary}
			</p>
			<p className="mt-3 text-(--mk-ink) text-[17px] leading-relaxed">
				<strong className="font-semibold">Best for:</strong>{" "}
				{item.bestFor}
			</p>
			<div className="mt-5 grid gap-6 sm:grid-cols-2">
				<div>
					<h4 className="font-semibold text-(--mk-ink) text-[16px]">
						What it does well
					</h4>
					<ul className="mt-2 list-disc space-y-1.5 ps-5 text-(--mk-ink-2) text-[15px] leading-relaxed">
						{item.strengths.map((point) => (
							<li key={point}>{point}</li>
						))}
					</ul>
				</div>
				<div>
					<h4 className="font-semibold text-(--mk-ink) text-[16px]">
						Limits
					</h4>
					<ul className="mt-2 list-disc space-y-1.5 ps-5 text-(--mk-ink-2) text-[15px] leading-relaxed">
						{item.limits.map((point) => (
							<li key={point}>{point}</li>
						))}
					</ul>
				</div>
			</div>
			<p className="mt-5 text-[15px]">
				{item.compare ? (
					<a
						href={item.compare}
						className="text-(--mk-ink) underline underline-offset-4"
					>
						{item.compareTitle}: full comparison
					</a>
				) : (
					<a
						href="/c/demo"
						className="text-(--mk-ink) underline underline-offset-4"
					>
						Try the demo store, Hephon Market
					</a>
				)}
			</p>
		</li>
	);
}
